import React, {useEffect} from 'react';
import {useParams} from 'react-router-dom';
import {BrokerReportGroup} from '../../types/BrokerReportGroup';
import {BrokerReportShareRecord} from '../../types/BrokerReportShareRecord';
import {ApiUtility} from "../common/ApiUtility";
import {ConditionalRenderer} from "../common/ConditionalRenderer";
import {Constants} from "../common/Constants";
import {useRenderableDataStore} from "../common/hooks/useRenderableDataStore";
import {MessageDisplayerUtility} from "../common/MessageDisplayerUtility";
import {OverlayLoadingIndicator} from "../common/OverlayLoadingIndicator";
import {PillButton} from "../common/PillButton";
import {BrokerPlanContent} from "./BrokerPlanContent";
import {BrokerReportSharingRecordsPanel} from "./BrokerReportSharingRecordsPanel";
import {ShareBrokerPlanModal} from "./ShareBrokerPlanModal";

type BrokerReportPageRouteParams = {
    brokerId: string;
    dateString: string;
};

export let BrokerReportPage = function () {
    const params = useParams<BrokerReportPageRouteParams>();
    const dataStore = useRenderableDataStore({
        lastRefreshTimestamp: undefined,
        brokerGroup: undefined as BrokerReportGroup | undefined,
        generatedAt: '',
        linkToken: null as string | null,
        shareRecords: [] as BrokerReportShareRecord[],
        creatingLink: false,
        showingSharingRecords: false,
        sharingChannel: null as 'text' | 'email' | null,
    });

    // Trigger reload of the report on mount and if the broker or the date in the URL changes.
    useEffect(
        function () {
            loadBrokerReport();
        },
        ['' + params.brokerId, '' + params.dateString]
    );

    function loadBrokerReport() {
        ApiUtility.getBrokerReportForDate('' + params.brokerId, '' + params.dateString)
            .then(function (reportData) {
                dataStore.updateAndRender({
                    lastRefreshTimestamp: (new Date()).getTime(),
                    brokerGroup: reportData.brokerGroups[0],
                    generatedAt: (new Date()).toISOString(),
                    linkToken: reportData.linkToken,
                    shareRecords: reportData.shareRecords,
                });
            })
            .catch(function (error) {
                dataStore.updateAndRender({lastRefreshTimestamp: (new Date()).getTime()});
                console && console.error(error);
                MessageDisplayerUtility.error(`An error occurred while loading the broker report.`, Constants.dispatchOrderMessageAdditionalConfig);
            });
    }

    function planLinkUrl() {
        return `${window.location.origin}/plans/${dataStore.data().linkToken}`;
    }

    function handleCreateLink() {
        // The link is created once and never regenerated, so a link already sent to a broker keeps working.
        if (dataStore.data().creatingLink || dataStore.data().linkToken) {
            return;
        }

        dataStore.updateAndRender({creatingLink: true});

        ApiUtility.createBrokerReportLink('' + params.brokerId, '' + params.dateString)
            .then(function (sharingState) {
                dataStore.updateAndRender({
                    creatingLink: false,
                    linkToken: sharingState.linkToken,
                    shareRecords: sharingState.shareRecords,
                });
            })
            .catch(function (error) {
                dataStore.updateAndRender({creatingLink: false});
                console && console.error(error);
                MessageDisplayerUtility.error(`An error occurred while creating the plan link.`, Constants.dispatchOrderMessageAdditionalConfig);
            });
    }

    function handleCopyLink() {
        window.navigator.clipboard.writeText(planLinkUrl())
            .then(function () {
                MessageDisplayerUtility.success(`Link copied`, Constants.dispatchOrderMessageAdditionalConfig);
            })
            .catch(function (error) {
                console && console.error(error);
                MessageDisplayerUtility.error(`An error occurred while copying the plan link.`, Constants.dispatchOrderMessageAdditionalConfig);
            });
    }

    function handlePrint() {
        ApiUtility.recordBrokerReportPrint('' + params.brokerId, '' + params.dateString, {})
            .then(function (sharingState) {
                dataStore.updateAndRender({shareRecords: sharingState.shareRecords});
                window.print();
            })
            .catch(function (error) {
                console && console.error(error);
                MessageDisplayerUtility.error(`An error occurred while recording the printed plan.`, Constants.dispatchOrderMessageAdditionalConfig);
            });
    }

    function handleShared(shareRecords: BrokerReportShareRecord[]) {
        dataStore.updateAndRender({shareRecords: shareRecords});
    }

    function handleToggleSharingRecords() {
        dataStore.updateAndRender({showingSharingRecords: !dataStore.data().showingSharingRecords});
    }

    if (dataStore.data().lastRefreshTimestamp) {
        return (
            <div className="broker-report-page">
                {/* @page cannot be scoped by selector, so only the mounted plan pages zero the printed page margins */}
                <style>{'@media print { @page { margin: 0; } }'}</style>
                <div className="broker-report-page-content">
                    <BrokerPlanContent
                        brokerGroup={dataStore.data().brokerGroup}
                        reportDate={'' + params.dateString}
                        generatedAt={dataStore.data().generatedAt}
                    />
                </div>
                <div className="broker-report-sharing-bar">
                    <div className="broker-report-sharing-bar-content">
                        <div className="broker-report-sharing-bar-content-left">
                            {(() => {
                                if (dataStore.data().linkToken) {
                                    return (
                                        <>
                                            <div className="broker-report-sharing-bar-options-label">SHARING OPTIONS
                                            </div>
                                            <div className="broker-report-sharing-bar-link-field">{planLinkUrl()}</div>
                                            <div className="broker-report-sharing-bar-icon-actions">
                                                <div
                                                    className="broker-report-sharing-bar-icon-action"
                                                    title="Copy link"
                                                    onClick={handleCopyLink}
                                                >
                                                    <img
                                                        className="broker-report-sharing-bar-icon copy-link"
                                                        src="/static/images/copy-light-gray.svg"
                                                        alt="Copy link"
                                                    />
                                                </div>
                                                <div className="broker-report-sharing-bar-icon-actions-rule"/>
                                                <div
                                                    className="broker-report-sharing-bar-icon-action"
                                                    title="Print"
                                                    onClick={handlePrint}
                                                >
                                                    <img
                                                        className="broker-report-sharing-bar-icon print"
                                                        src="/static/images/print-light-gray.svg"
                                                        alt="Print"
                                                    />
                                                </div>
                                                <div
                                                    className="broker-report-sharing-bar-icon-action broker-report-sharing-bar-share-by-text-action"
                                                    title="Share by text"
                                                    onClick={() => dataStore.updateAndRender({sharingChannel: 'text'})}
                                                >
                                                    <img
                                                        className="broker-report-sharing-bar-icon share-by-text"
                                                        src="/static/images/mobile-screen-light-gray.svg"
                                                        alt="Share by text"
                                                    />
                                                </div>
                                                <div
                                                    className="broker-report-sharing-bar-icon-action broker-report-sharing-bar-share-by-email-action"
                                                    title="Share by email"
                                                    onClick={() => dataStore.updateAndRender({sharingChannel: 'email'})}
                                                >
                                                    <img
                                                        className="broker-report-sharing-bar-icon share-by-email"
                                                        src="/static/images/envelope-light-gray.svg"
                                                        alt="Share by email"
                                                    />
                                                </div>
                                            </div>
                                        </>
                                    )
                                } else {
                                    return (
                                        <div className="broker-report-sharing-bar-status">
                                            <img
                                                className="broker-report-sharing-bar-status-icon"
                                                src={
                                                    dataStore.data().creatingLink
                                                        ? Constants.loadingIndicatorOnGraySrc
                                                        : '/static/images/status-records-icon.svg'
                                                }
                                            />
                                            <div className="broker-report-sharing-bar-status-text">
                                                {dataStore.data().creatingLink ? 'Creating the link…' : 'Not yet shared'}
                                            </div>
                                        </div>
                                    )
                                }
                            })()}
                        </div>
                        <div className="broker-report-sharing-bar-content-right">
                            {(() => {
                                if (dataStore.data().linkToken) {
                                    return (
                                        <div
                                            className="broker-report-sharing-bar-records-control"
                                            onClick={handleToggleSharingRecords}
                                        >
                                            <div className="broker-report-sharing-bar-records-control-text">
                                                SHARE RECORDS ({dataStore.data().shareRecords.length})
                                            </div>
                                            <img
                                                className={
                                                    `broker-report-sharing-bar-records-control-chevron${dataStore.data().showingSharingRecords ? ' open' : ''}`
                                                }
                                                src="/static/images/chevron-down-light-gray.svg"
                                            />
                                        </div>
                                    )
                                } else {
                                    return (
                                        <PillButton
                                            containerClasses="broker-report-sharing-bar-share-plan-button"
                                            buttonClasses="solid-green-button"
                                            buttonText="SHARE PLAN"
                                            onClick={handleCreateLink}
                                            disabled={dataStore.data().creatingLink}
                                        />
                                    )
                                }
                            })()}
                        </div>
                    </div>
                </div>
                <BrokerReportSharingRecordsPanel
                    open={dataStore.data().showingSharingRecords}
                    shareRecords={dataStore.data().shareRecords}
                    onClose={handleToggleSharingRecords}
                />
                <ConditionalRenderer if={dataStore.data().sharingChannel} renderer={() => (
                    <ShareBrokerPlanModal
                        channel={dataStore.data().sharingChannel as 'text' | 'email'}
                        broker={dataStore.data().brokerGroup?.broker}
                        dateString={'' + params.dateString}
                        planLinkUrl={planLinkUrl()}
                        close={() => dataStore.updateAndRender({sharingChannel: null})}
                        onShared={handleShared}
                    />
                )}/>
            </div>
        );
    } else {
        return (
            <OverlayLoadingIndicator/>
        );
    }
};
